import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import awsconfig from './utils/awsConfig';
import { Toaster } from 'react-hot-toast';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import MainLayout from './components/Layout/MainLayout';
import { LoadingProvider } from './context/LoadingContext';
import { UserSettingsProvider, useUserSettingsContext } from './context/UserSettingsContext';
import { resolveNativeReportGroups, hasNativeReportGroup, hasForecastingGrant } from './config/reportGroupAccess';
import { StreamChatProvider } from './context/StreamChatContext';
import { ThemeProvider } from './context/ThemeContext';
import { AssistantContextProvider } from './assistant/context';
import AssistantLauncher from './components/AIChat/AssistantLauncher';
import './assets/css/chat.css';
import './styles/theme.css';
import './styles/appShell.css';
import SettingsLayout from './components/Settings/SettingsLayout';
import AccountSettings from './components/Settings/AccountSettings';
import AppearanceSettings from './components/Settings/AppearanceSettings';
import SecuritySettings from './components/Settings/SecuritySettings';
import MagicLinkHandler from './components/MagicLinkHandler';
import EmailNotificationsSettings from './components/Settings/EmailNotificationsSettings';
import ApiKeysSettings from './components/Settings/ApiKeysSettings';
import UsersPage from './components/Admin/users/UsersPage';
import OrganizationsPage from './components/Admin/orgs/OrganizationsPage';
import OrganizationDetailPage from './components/Admin/orgs/OrganizationDetailPage';
import LocationCompSets from './components/Admin/LocationCompSets';
import AiSkillsPage from './components/Admin/skills/AiSkillsPage';
import StreamChat from './pages/StreamChat';
import Budgeting from './pages/Budgeting';
import ChartOfAccountsPage from './pages/ChartOfAccountsPage';
import RevenueTypesPage from './pages/RevenueTypesPage';
import ForecastSourcesPage from './pages/ForecastSourcesPage';
import JournalTypesPage from './pages/JournalTypesPage';
import PositionDepartmentMappingPage from './pages/PositionDepartmentMappingPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import LaborBudgeting from './pages/LaborBudgeting';
import EtlConfigPage from './pages/EtlConfigPage';
import EtlRedirect from './pages/etlConfig/EtlRedirect';
import CommentaryPackagePrint from './components/FinancialAnalyst/CommentaryPackagePrint';
import RevenueExportPage from './pages/RevenueExportPage';
import JournalExportPage from './pages/JournalExportPage';
import JournalOverviewPage from './pages/JournalOverviewPage';
import ForecastModuleV3 from './modules/forecast-v3/ForecastModuleV3';
import { trackActivity } from './utils/activityLogger';
import tokenRefreshManager from './utils/tokenRefresh';

import DashboardPage from './pages/Dashboard';
import NightAuditPage from './pages/NightAuditPage';
import NightAuditAdminPage from './pages/NightAuditAdminPage';
import MobileAppBanner from './components/MobileAppBanner';
import MobileAppModal from './components/MobileAppModal';
import EmulationBanner from './components/EmulationBanner';

// Native report pages (Power BI replacement) — lazy-loaded
const ReportsCatalogPage = React.lazy(() => import('./pages/reports/ReportsCatalogPage'));
const AnalyticsPage = React.lazy(() => import('./pages/analytics/AnalyticsPage'));
const FinancialPnlPage = React.lazy(() => import('./pages/reports/FinancialPnlPage'));
const FinancialTrendsPage = React.lazy(() => import('./pages/reports/FinancialTrendsPage'));
const GlDetailPage = React.lazy(() => import('./pages/reports/GlDetailPage'));
const LaborManagementPage = React.lazy(() => import('./pages/reports/LaborManagementPage'));
const ReviewsPage = React.lazy(() => import('./pages/reports/ReviewsPage'));
const RevenueWorkspacePage = React.lazy(() => import('./pages/reports/RevenueWorkspacePage'));
const RevenueForecastPage = React.lazy(() => import('./pages/reports/RevenueForecastPage'));
const NightAuditsPage = React.lazy(() => import('./pages/reports/NightAuditsPage'));
const MarketPerformancePage = React.lazy(() => import('./pages/reports/MarketPerformancePage'));
const WorkflowRunPage = React.lazy(() => import('./pages/WorkflowRunPage'));

// Chunk-load fallback shaped like a report page (title, chip rail, KPI card,
// table card) so the swap never flashes an off-style block.
// The New look swaps in the report toolbar + tile/chart/table rhythm (pure
// CSS so the kit's chart bundle stays out of the main chunk).
const reportRouteFallback = (
  <>
    <div className="rn-route-fallback rn-sk-classic-only">
      <div className="lodge-skeleton" style={{ height: 30, width: '45%', borderRadius: 8 }} />
      <div className="lodge-skeleton" style={{ height: 34, borderRadius: 999 }} />
      <div className="lodge-skeleton" style={{ height: 140, borderRadius: 16 }} />
      <div className="lodge-skeleton" style={{ height: 280, borderRadius: 16 }} />
    </div>
    <div className="rn-route-fallback-inset rn-sk-inset-only" aria-busy="true" aria-label="Loading report">
      <div className="rfi-toolbar">
        <span className="rn-sk" style={{ width: 220, height: 26, borderRadius: 8 }} />
        <span className="rn-sk" style={{ width: 150, height: 26, borderRadius: 8 }} />
        <span className="rn-sk" style={{ width: 90, height: 26, borderRadius: 8 }} />
      </div>
      <div className="rfi-row rfi-tiles">
        {[0, 1, 2, 3, 4].map((i) => (
          <div className="rfi-card" key={i}>
            <span className="rn-sk" style={{ width: '46%', height: 9 }} />
            <span className="rn-sk" style={{ width: '64%', height: 24, marginTop: 12 }} />
            <span className="rn-sk" style={{ width: '100%', height: 36, marginTop: 14 }} />
          </div>
        ))}
      </div>
      <div className="rfi-row rfi-charts">
        {[0, 1, 2].map((i) => (
          <div className="rfi-card" key={i}>
            <span className="rn-sk" style={{ width: '40%', height: 12 }} />
            <span className="rn-sk" style={{ width: '100%', height: 180, marginTop: 16 }} />
          </div>
        ))}
      </div>
      <div className="rfi-card rfi-table">
        <span className="rn-sk" style={{ width: 180, height: 12 }} />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span className="rn-sk" key={i} style={{ width: '100%', height: 10, marginTop: 18 }} />
        ))}
      </div>
    </div>
  </>
);

const lazyReportRoute = (Component, props) => (
  <React.Suspense fallback={reportRouteFallback}>
    <Component {...(props || {})} />
  </React.Suspense>
);

/**
 * Forecasting carries the same per-user grant as the Financial report — the
 * 'financial' native report group — so the sidebar hiding it (navConfig.js)
 * isn't the only gate; a typed URL lands here too. Fails OPEN: an
 * unresolvable/absent group list is the unrestricted transition posture, and
 * while initData is still loading there is nothing to judge on yet.
 */
// Budgeting & Forecasting: an explicit per-user grant (User setup → Report
// Access) opens it outright; otherwise the legacy door still needs the
// Financial report group here and the PostHog flag inside the module.
const RequireForecastingAccess = ({ children }) => {
  const { userSettings, currentOrganization } = useUserSettingsContext();
  const groups = resolveNativeReportGroups(currentOrganization, userSettings);
  if (hasForecastingGrant(groups)) return children;
  if (!hasNativeReportGroup(groups, 'financial')) return <Navigate to="/dashboard" replace />;
  return children;
};

// Legacy/deep assistant URLs now open the one app-level assistant instead of
// mounting a second chat page. The launcher survives this redirect, receives
// the requested thread, and opens in the full-screen workspace.
const AssistantRouteBridge = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', {
      detail: { expanded: true, threadId: sessionId || null }
    }));
    navigate('/dashboard/revenue', { replace: true });
  }, [navigate, sessionId]);
  return null;
};

Amplify.configure(awsconfig);

// Global error handlers for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  // Prevent the default browser error handling
  event.preventDefault();
});

// Helper function to get userId from localStorage as a fallback
const getUserIdFromLocalStorage = () => {
  try {
    const lastAuthUserKey = Object.keys(localStorage).find(key => key.endsWith('.LastAuthUser'));
    return lastAuthUserKey ? localStorage.getItem(lastAuthUserKey) : null;
  } catch (error) {
    console.error("Error accessing localStorage:", error);
    return null;
  }
};

// New component to contain the logic that uses the context
function AppContent() {
  const isMobile = window.innerWidth <= 768;
  const location = useLocation();
  const { userSettings, loading } = useUserSettingsContext(); // Now called inside the Provider
  const aiAssistantEnabled = useFeatureFlagEnabled('ai-assistant');
  const nightAuditRiskEnabled = useFeatureFlagEnabled('night-audit-risk');
  const nightAuditConfigEnabled = useFeatureFlagEnabled('night-audit-config');
  const securitySettingsEnabled = useFeatureFlagEnabled('security-settings');
  const apiKeyGenEnabled = useFeatureFlagEnabled('api-key-gen');
  const adminOpsEnabled = useFeatureFlagEnabled('admin-ops');

  // Initialize token refresh when user is authenticated
  useEffect(() => {
    if (!loading && userSettings) {

      tokenRefreshManager.initializeTokenRefresh();
      
      // Cleanup function to stop token refresh when component unmounts
      return () => {

        tokenRefreshManager.stopTokenRefresh();
      };
    }
  }, [loading, userSettings]);

  // Effect to track page navigation - moved here
  useEffect(() => {
    // Log every time the effect runs due to dependency changes

    // Log current context state

    // Wait for user settings to load and ensure user is logged in
    if (!loading && userSettings) {
      // Determine the userId: Prefer context, fallback to localStorage
      let userIdToLog = userSettings.cognitoId; 
      if (!userIdToLog) {
        console.warn('User context missing cognitoId, attempting localStorage fallback.');
        userIdToLog = getUserIdFromLocalStorage();
      }

      // Ensure we have an email from context
      const userEmailToLog = userSettings.email;

      // Check if we have the minimum required info (userId, userEmail, path)
      if (userIdToLog && userEmailToLog) {

          // Pass determined user ID and email from context
          trackActivity({
            urlPath: location.pathname + location.search + location.hash,
            userId: userIdToLog, 
            userEmail: userEmailToLog
            // Add pbi details here when tracking PBI interactions
          });
      } else {
         console.warn('AppContent useEffect - Condition PARTIALLY MET, but missing required ID or Email after fallback. Cannot log activity.', { userId: userIdToLog, email: userEmailToLog });
      }

    } else if (loading) {

    } else if (!userSettings) {

    }
  }, [location, userSettings, loading]);

  return (
    <>
      <EmulationBanner />
      {/* The assistant is docked to the app frame, not to a page. It renders
          outside <Routes> so it survives navigation — the whole point of an
          ambient assistant is that it does not restart when you move.

          Always mounted, because this corner also holds SUPPORT: Intercom boots
          with its own launcher hidden, so gating this away would take the support
          channel with it. `=== true` fails closed — while PostHog resolves the
          flag the corner shows support only, and the assistant appears once the
          flag is actually on rather than flashing for someone without it. */}
      <AssistantLauncher aiEnabled={aiAssistantEnabled === true} />
      <Routes>
        {/* Public route */}
        <Route path="/login" element={<Login />} />
        {/* Welcome-email one-time link: /welcome#t=<token> (see utils/inviteToken) */}
        <Route path="/welcome" element={<Login />} />
        <Route path="/auth/verify" element={<MagicLinkHandler />} />

        {/* Test route for Stream Chat */}
        <Route path="/test/stream-chat" element={<ProtectedRoute><StreamChat /></ProtectedRoute>} />

        {/* Standalone print/export view (no app chrome — opens in its own tab) */}
        <Route path="/financial-analyst/export" element={<ProtectedRoute><CommentaryPackagePrint /></ProtectedRoute>} />

        {/* Protected routes with different layouts for mobile/desktop */}
        <Route element={<ProtectedRoute>
          <MainLayout isMobile={isMobile} />
        </ProtectedRoute>}>
          <Route index element={<Navigate to="/dashboard/revenue" replace />} />
          <Route path="analytics" element={lazyReportRoute(AnalyticsPage)} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="dashboard/overview" element={<DashboardPage />} />
          <Route path="dashboard/revenue" element={<DashboardPage />} />
          <Route path="dashboard/operations" element={<DashboardPage />} />
          <Route path="dashboard/financial" element={<DashboardPage />} />
          <Route path="dashboard/forecast" element={<DashboardPage />} />
          {nightAuditRiskEnabled !== false && (
            <Route path="dashboard/night-audit" element={<NightAuditPage />} />
          )}
          {/* Native report pages */}
          <Route path="reports" element={lazyReportRoute(ReportsCatalogPage)} />
          <Route path="reports/financial/pnl" element={lazyReportRoute(FinancialPnlPage)} />
          <Route path="reports/financial/trends" element={lazyReportRoute(FinancialTrendsPage)} />
          <Route path="reports/financial/gl-detail" element={lazyReportRoute(GlDetailPage)} />
          <Route path="reports/operations/labor" element={lazyReportRoute(LaborManagementPage)} />
          <Route path="reports/operations/reviews" element={lazyReportRoute(ReviewsPage)} />
          {/* Unified Revenue workspace (Overview / Trend / Detail tabs); the
              legacy trends URL lands on the Trend tab so old links and
              subscriptions keep working */}
          <Route path="reports/revenue/overview" element={lazyReportRoute(RevenueWorkspacePage)} />
          <Route path="reports/revenue/trends" element={lazyReportRoute(RevenueWorkspacePage, { defaultView: 'trend' })} />
          <Route path="reports/revenue/forecast" element={lazyReportRoute(RevenueForecastPage)} />
          <Route path="reports/revenue/night-audits" element={lazyReportRoute(NightAuditsPage)} />
          <Route path="reports/revenue/market-performance" element={lazyReportRoute(MarketPerformancePage)} />
          <Route path="workflow-runs/:runId" element={lazyReportRoute(WorkflowRunPage)} />
          <Route path="chat" element={<StreamChat />} />
          {aiAssistantEnabled !== false && (
            <>
              <Route path="ai-assistant" element={<AssistantRouteBridge />} />
              <Route path="ai-assistant/:sessionId" element={<AssistantRouteBridge />} />
            </>
          )}
          <Route path="budgeting" element={<Budgeting />} />
          <Route path="forecast-v3" element={<RequireForecastingAccess><ForecastModuleV3 /></RequireForecastingAccess>} />
          <Route path="labor-budgets" element={<LaborBudgeting />} />
          <Route path="chart-of-accounts" element={<ChartOfAccountsPage />} />
          <Route path="revenue-types" element={<RevenueTypesPage />} />
          <Route path="forecast-sources" element={<ForecastSourcesPage />} />
          <Route path="journal-types" element={<JournalTypesPage />} />
          <Route path="revenue-export" element={<RevenueExportPage />} />
          {/* Journal Export module: overview (day-by-day) + detail (line-level) */}
          <Route path="journal-export" element={<JournalOverviewPage />} />
          <Route path="journal-export/detail" element={<JournalExportPage />} />
          <Route path="position-department-mapping" element={<PositionDepartmentMappingPage />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="subscriptions/:tab" element={<SubscriptionsPage />} />
          {/* ETL Configuration — one page, tabbed. The four legacy paths below
              redirect into it so existing links, bookmarks and the alert emails
              that point at /file-tracking keep working. */}
          <Route path="etl-config" element={<EtlConfigPage />} />
          <Route path="etl-config/schema" element={<EtlConfigPage />} />
          <Route path="etl-config/schema/:hotelId" element={<EtlConfigPage />} />
          <Route path="etl-config/:tab" element={<EtlConfigPage />} />
          <Route path="data-import" element={<EtlRedirect to="upload" />} />
          <Route path="import-review" element={<EtlRedirect to="review" />} />
          <Route path="schema-builder" element={<EtlRedirect to="schema" />} />
          <Route path="schema-builder/:hotelId" element={<EtlRedirect to="schema" withHotelId />} />
          <Route path="file-tracking/:view?" element={<EtlRedirect legacyFileTracking />} />

          {/* Settings Routes */}
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="/settings/account" replace />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="appearance" element={<AppearanceSettings />} />
            {securitySettingsEnabled !== false && (
              <Route path="security" element={<SecuritySettings />} />
            )}
            <Route path="notifications" element={<EmailNotificationsSettings />} />
            <Route path="email-notifications" element={<EmailNotificationsSettings />} />
            {apiKeyGenEnabled && (
              <Route path="api-keys" element={<ApiKeysSettings />} />
            )}
          </Route>

          {/* Admin Routes - integrated into main layout */}
          <Route path="admin" element={<AdminProtectedRoute />}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="comp-sets" element={<LocationCompSets />} />
            <Route path="ai-skills" element={<AiSkillsPage />} />
            {nightAuditConfigEnabled !== false && (
              <Route path="night-audit-config" element={<NightAuditAdminPage />} />
            )}
            {/* Wait for flags: a direct load must not fall through to the catch-all */}
            {adminOpsEnabled !== false && (
              <>
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="organizations/:orgId" element={<OrganizationDetailPage />} />
                {/* Operations + Refresh merged into Organizations */}
                <Route path="operations" element={<Navigate to="/admin/organizations" replace />} />
                <Route path="refresh" element={<Navigate to="/admin/organizations" replace />} />
              </>
            )}
          </Route>
        </Route>

        {/* Catch all redirects to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* Mobile app redirect components - Choose one or use both */}
      {/* Option 1: Modal (shows once on first visit) */}
      <MobileAppModal />

      {/* Option 2: Banner (sticky at bottom, less intrusive) */}
      {/* <MobileAppBanner /> */}

      <Toaster position="top-right" />
    </>
  );
}


// Main App component now only sets up providers
function App() {
  return (
    <ThemeProvider>
      <LoadingProvider>
        <UserSettingsProvider>
          <StreamChatProvider>
            {/* App-level so any assistant surface — the Forecast popup today,
                an app-wide shell next — can read the current page's context
                without importing that page's own React context. */}
            <AssistantContextProvider>
              <AppContent /> {/* Render the child component here */}
            </AssistantContextProvider>
          </StreamChatProvider>
        </UserSettingsProvider>
      </LoadingProvider>
    </ThemeProvider>
  );
}

export default App; 
